import { createContext, useState, useContext, useCallback, useRef, memo } from 'react';
import Toast from '../components/ui/Toast';

const ToastContext = createContext(null);

// Memoized toast item component to prevent unnecessary re-renders
const ToastItem = memo(({ toast, onRemove }) => {
  const handleClose = useCallback(() => {
    onRemove(toast.id);
  }, [toast.id, onRemove]);

  return (
    <Toast
      message={toast.message}
      type={toast.type}
      duration={toast.duration}
      onClose={handleClose}
    />
  );
});

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  // Add a counter to ensure unique IDs even when created in the same millisecond
  const toastCounter = useRef(0);
  
  const addToast = useCallback((message, type = 'info', duration = 5000) => {
    // Create a truly unique ID by combining timestamp with a counter
    const id = `${Date.now()}_${toastCounter.current++}`;
    setToasts(prevToasts => [...prevToasts, { id, message, type, duration }]);
    return id;
  }, []);
  
  const removeToast = useCallback((id) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);
  
  // Convenience methods for different toast types
  const showSuccess = useCallback((message, duration) => {
    return addToast(message, 'success', duration);
  }, [addToast]);
  
  const showError = useCallback((message, duration) => {
    return addToast(message, 'error', duration);
  }, [addToast]);
  
  const showWarning = useCallback((message, duration) => {
    return addToast(message, 'warning', duration);
  }, [addToast]);
  
  const showInfo = useCallback((message, duration) => {
    return addToast(message, 'info', duration);
  }, [addToast]);
  
  return (
    <ToastContext.Provider value={{ showSuccess, showError, showWarning, showInfo }}>
      {children}
      <div className="fixed inset-0 z-50 flex flex-col items-end justify-start p-6 pointer-events-none space-y-4">
        {toasts.map(toast => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onRemove={removeToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
